import React from 'react';

import {
  ImageField,
  ImageInput,
  ImageInputProps,
  Loading,
  setSubmissionErrors,
  useInput,
} from 'react-admin';

import { useFormContext } from 'react-hook-form';
import { Prefix } from '../../../graphql/types';
import { useUploadFile } from '../../../hooks/useUploadFile';

interface ImageUploadInputProps extends ImageInputProps {
  prefix: Prefix;
}

const ImageUploadInput: React.FC<ImageUploadInputProps> = ({
  prefix,
  source,
  ...props
}) => {
  const [loading, setLoading] = React.useState(false);

  const { setError } = useFormContext();
  const { field } = useInput({ source });
  const { uploadImage } = useUploadFile();

  const handleChange = React.useCallback(
    async (value?: File) => {
      if (value instanceof File) {
        setLoading(true);
        setSubmissionErrors({ [source]: '' }, setError);

        let key: string | null = null;
        let url: string | null = null;
        try {
          const result = await uploadImage({
            file: value,
            prefix,
            urlWithDimensions: prefix !== Prefix.ExerciseThumbnail,
          });
          key = result.key;
          url = result.url;
        } catch (error) {
          if (error instanceof Error) {
            setSubmissionErrors({ [source]: error.message }, setError);
          } else {
            setSubmissionErrors(
              { [source]: 'Unexpected error during image upload' },
              setError,
            );
          }
          setLoading(false);
          return;
        }
        // In order to support thumbnail preview as well as sending the correct value
        // we need to treat the field as an object within React-Admin
        // `key` is what is stored in the database/passed into mutations
        // `src` is what is used for the thumbnail preview
        if (prefix === Prefix.ExerciseThumbnail) {
          field.onChange({
            key: key && key.includes('/') ? key.split('/')[1] : null,
            url: url,
          });
        } else {
          field.onChange({ key, url });
        }
      } else {
        field.onChange(null);
      }
      setLoading(false);
    },
    [uploadImage, field, prefix, setError, source],
  );
  return (
    <ImageInput
      source={source}
      onChange={handleChange}
      accept={{
        'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
      }}
      {...props}
    >
      {loading ? <Loading /> : <ImageField source={'url'} title="title" />}
    </ImageInput>
  );
};

export default ImageUploadInput;
